// Star breakdown for the product reviews section.
// Reads the histogram Judge.me prints into its review widget block (jdgm.data.reviewWidget).
// That's the app's own page data, not a public API, so anything unexpected leaves the breakdown hidden.
if (!customElements.get('product-reviews-breakdown')) {
  customElements.define(
    'product-reviews-breakdown',
    class ProductReviewsBreakdown extends HTMLElement {
      connectedCallback() {
        const data = window.jdgm?.data?.reviewWidget?.[this.dataset.productId];
        const histogram = Array.isArray(data?.histogram) ? data.histogram : [];
        const rows = histogram
          .map((row) => ({ rating: Number(row.rating), count: Number(row.frequency) }))
          .filter((row) => row.rating >= 1 && Number.isFinite(row.count))
          .sort((a, b) => b.rating - a.rating);
        const total = rows.reduce((sum, row) => sum + row.count, 0);
        const list = this.querySelector('ol');

        if (!list || rows.length === 0 || total === 0) return;

        list.replaceChildren(...rows.map((row) => this.renderRow(row, total)));
        this.hidden = false;
      }

      renderRow({ rating, count }, total) {
        const item = document.createElement('li');
        item.className = 'product-reviews__bar-row';

        const label = document.createElement('span');
        label.className = 'product-reviews__bar-label';
        label.textContent = `${rating} ${rating === 1 ? 'star' : 'stars'}`;

        const bar = document.createElement('span');
        bar.className = 'product-reviews__bar';
        bar.setAttribute('aria-hidden', 'true');
        bar.style.setProperty('--fill', `${Math.round((count / total) * 100)}%`);

        const value = document.createElement('span');
        value.className = 'product-reviews__bar-count';
        value.textContent = count;

        const hint = document.createElement('span');
        hint.className = 'visually-hidden';
        hint.textContent = count === 1 ? ' review' : ' reviews';
        value.append(hint);

        item.append(label, bar, value);
        return item;
      }
    }
  );
}

// Sort menu for the Judge.me widget.
// Judge.me sorts with a native <select>, whose menu the browser draws. This swaps that menu for a
// panel matching the theme's filter popovers. Choosing an option sets the select and fires its change
// event, so Judge.me still does the sorting. The widget renders late and can re-render, so new sort
// controls are picked up as they appear.
if (!customElements.get('product-reviews-sort')) {
  customElements.define(
    'product-reviews-sort',
    class ProductReviewsSort extends HTMLElement {
      connectedCallback() {
        this.enhanceAll();
        this.observer = new MutationObserver(() => this.enhanceAll());
        this.observer.observe(this, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      enhanceAll() {
        this.querySelectorAll('.jm-sort-filter:not([data-hott-sort])').forEach((control) => {
          const select = control.querySelector('select');
          if (select) this.enhance(control, select);
        });
      }

      enhance(control, select) {
        control.setAttribute('data-hott-sort', '');
        control.classList.add('product-reviews-sort');
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');

        const id = `ReviewSort-${select.id || Math.random().toString(36).slice(2)}`;
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'product-reviews-sort__toggle';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', id);

        const panel = document.createElement('div');
        panel.className = 'product-reviews-sort__panel';
        panel.id = id;
        panel.hidden = true;

        const close = (returnFocus) => {
          if (panel.hidden) return;
          panel.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          document.removeEventListener('click', onOutside, true);
          if (returnFocus) toggle.focus();
        };
        const onOutside = (event) => {
          if (!control.contains(event.target)) close(false);
        };

        const label = () => {
          const current = select.options[select.selectedIndex];
          toggle.setAttribute('aria-label', current ? `Sort reviews: ${current.text.trim()}` : 'Sort reviews');
        };

        // Built on each open so it follows whatever options and value Judge.me currently has
        const open = () => {
          const list = document.createElement('ul');
          list.className = 'list-unstyled';
          list.setAttribute('role', 'list');
          [...select.options].forEach((option) => {
            if (!option.value) return;
            const item = document.createElement('li');
            item.innerHTML = `<label class="product-reviews-sort__option"><input type="radio" name="${id}"><span class="product-reviews-sort__dot" aria-hidden="true"></span><span></span></label>`;
            const input = item.querySelector('input');
            input.value = option.value;
            input.checked = option.value === select.value;
            item.querySelector('span:last-child').textContent = option.text.trim();
            list.append(item);
          });

          const fieldset = document.createElement('fieldset');
          fieldset.innerHTML = '<legend class="visually-hidden">Sort reviews</legend>';
          fieldset.append(list);
          panel.replaceChildren(fieldset);

          panel.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');
          document.addEventListener('click', onOutside, true);
          (panel.querySelector('input:checked') || panel.querySelector('input'))?.focus();
        };

        // Arrow keys move through the options and apply them, so the panel only closes on a click, Enter or Space
        let closeOnChange = false;
        panel.addEventListener('pointerdown', () => (closeOnChange = true));
        panel.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const input = event.target.closest('input');
            if (input && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            close(true);
          } else if (event.key === 'Tab') {
            close(false);
          }
        });
        panel.addEventListener('change', (event) => {
          select.value = event.target.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          label();
          if (closeOnChange) close(true);
          closeOnChange = false;
        });

        toggle.addEventListener('click', () => (panel.hidden ? open() : close(true)));
        toggle.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') close(true);
        });
        select.addEventListener('change', label);

        label();
        control.append(toggle, panel);
      }
    }
  );
}
